/**
 * The shell: who is signed in, where they can go, and the gate in front of everything.
 *
 * Navigation is a fixed bar along the bottom of the screen rather than a row of tabs under
 * the masthead (D-82). The primary device is a phone held in one hand, and the top of a
 * phone is the part of it a thumb cannot reach. Sign out went with the change: it was the
 * only other thing in the masthead, it is not navigation, and it now sits at the foot of
 * Profile, which is the screen it belongs to.
 *
 * The unauthenticated state is a real sign-in form now (D-61) rather than an instruction
 * to go and message the bot, so the portal stands on its own for someone who has never
 * used WhatsApp with it.
 */

import { useEffect } from "react";
import { NavLink, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { isOnboarded } from "@altrtrack/core";
import { useAuth } from "./auth.js";
import { CatalogContext, useCatalogEdits, useProfile } from "./data.js";
import { ensureProfile } from "./write.js";
import { AuthPage } from "./pages/AuthPage.js";
import { SignIn } from "./components/SignIn.js";
import { TodayPage } from "./pages/TodayPage.js";
import { DayPage } from "./pages/DayPage.js";
import { ProgressPage } from "./pages/ProgressPage.js";
import { ProfilePage } from "./pages/ProfilePage.js";
import { OnboardingPage } from "./pages/OnboardingPage.js";
import { NewPlanPage, PlanPage } from "./pages/PlanPage.js";

export function App() {
  const { user } = useAuth();

  return (
    <div className="shell">
      <Routes>
        <Route path="/auth" element={<AuthPage />} />
        <Route
          path="*"
          element={
            user === undefined ? (
              <Restoring />
            ) : user === null ? (
              <SignedOut />
            ) : (
              <SignedIn uid={user.uid} phoneNumber={user.phoneNumber} />
            )
          }
        />
      </Routes>
    </div>
  );
}

function Restoring() {
  // Firebase restores a persisted session asynchronously. Rendering the signed-out
  // instruction during that gap would tell a signed-in user to go and get a link.
  return <p className="sub centered">Opening your log…</p>;
}

function SignedOut() {
  // No wrapper. `.signin` is already a full-height centred column, and nesting it inside
  // `.centered` meant a `100dvh` box centred inside a `70dvh` one — which on a phone put
  // the wordmark above the top of the screen.
  return <SignIn />;
}

function SignedIn({ uid, phoneNumber }: { uid: string; phoneNumber: string | null }) {
  const profile = useProfile(uid);
  // Above every early return below (the hook-order rule). The state lives here, so an
  // install re-renders everything under the shell, not only what reads the context.
  const catalog = useCatalogEdits(uid);
  /*
   * The day editor is reached from Today and from Progress, but it is its own route — so
   * with nothing else said, standing in it lights no tab at all and the bar reads as
   * though the app has no current section. Whichever tab's link opened it stamps
   * `state.from` on the navigation, so the bar lights the tab the user actually came
   * from rather than defaulting to Today for every entrance.
   */
  const location = useLocation();
  const editingADay = location.pathname.startsWith("/day/");
  const cameFromProgress = (location.state as { from?: string } | null)?.from === "progress";

  // A number verified here that has never messaged the bot has no profile yet.
  useEffect(() => {
    if (profile.loading || profile.data || !phoneNumber) return;
    void ensureProfile(uid, phoneNumber);
  }, [uid, phoneNumber, profile.loading, profile.data]);

  /*
   * The onboarding gate (B-38, D-90). Below every hook in this component and above every
   * return that renders the app — the hook-order rule this project has broken three times
   * (D-56, B-30) makes the order of these two facts the only safe one.
   *
   * It waits for the profile rather than assuming: a document still loading, or not yet
   * created by the `ensureProfile` above, is not a document that has skipped onboarding.
   * Once it exists, `isOnboarded` reads the one timestamp — so an account the bot created
   * months ago, which has genuinely never been asked these questions, is asked them on its
   * first dashboard sign-in.
   */
  if (profile.loading) return <Restoring />;
  if (profile.data && !isOnboarded(profile.data)) return <OnboardingPage uid={uid} />;

  /*
   * The wordmark is on Profile only (D-95). On Today and Progress it spent a band at the
   * top of a phone restating the name of the app the user is already in, above the
   * content they came for. `.bare` gives those screens the top inset the masthead was
   * carrying for them.
   */
  const branded = location.pathname === "/profile";

  return (
    <CatalogContext.Provider value={catalog}>
      {branded && (
        <header className="masthead">
          <h1 className="wordmark">altrtrack</h1>
        </header>
      )}

      <main className={branded ? "screen" : "screen bare"}>
        <Routes>
          <Route path="/" element={<TodayPage uid={uid} />} />
          <Route path="/day/:date" element={<DayPage uid={uid} />} />
          <Route path="/progress" element={<ProgressPage uid={uid} />} />
          <Route path="/profile" element={<ProfilePage uid={uid} />} />
          <Route path="/plan" element={<PlanPage uid={uid} />} />
          <Route path="/plan/new" element={<NewPlanPage uid={uid} />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>

      {/*
        Below the routes rather than above them, so the reading order a screen reader and
        the tab key follow is heading, then content, then navigation — the bar's position
        on screen is the stylesheet's business.
      */}
      <nav className="tabbar" aria-label="Sections">
        <NavLink
          to="/"
          end
          className={({ isActive }) => (isActive || (editingADay && !cameFromProgress) ? "active" : "")}
        >
          Today
        </NavLink>
        <NavLink to="/progress" className={({ isActive }) => (isActive || cameFromProgress ? "active" : "")}>
          Progress
        </NavLink>
        {/* The plan screens are reached from Profile and belong to it (B-48). */}
        <NavLink to="/profile" className={({ isActive }) => (isActive || location.pathname.startsWith("/plan") ? "active" : "")}>
          Profile
        </NavLink>
      </nav>
    </CatalogContext.Provider>
  );
}
